import { useGetAreaId } from 'pages/documents_v2/views/edit_area/hooks/useGetAreaId';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { saveTokenData } from 'api/AuthApi';
import { useGetArea } from 'pages/documents_v2/hooks/useGetArea';
import { AreaContext } from 'pages/documents_v2/views/edit_area/context/AreaContext';
import { useGetAreaBlocks } from 'pages/documents_v2/hooks/useGetAreaBlocks';
import { useGetBaseVariant } from 'pages/documents_v2/hooks/useGetBaseVariant';
import DocumentHelper from 'pages/global/DocumentHelper';
import { useLocalStorage } from 'hooks/useLocalStorage';
import { EditAreaContext } from 'pages/documents_v2/views/edit_area/context/EditAreaContext';
import { AreaBlocks } from 'pages/documents_v2/views/edit_area/AreaBlocks';
import { Formik } from 'formik';
import { AreaTitle } from 'pages/documents_v2/views/edit_area/AreaTitle';
import { useGetDocument } from 'pages/documents_v2/hooks/useGetDocument';
import VariantViewButtons from 'pages/documents_v2/views/edit_area/VariantViewButtons';
import LoadingSpinner from 'pages/global/LoadingSpinner';
import { getEnv } from 'config/Env';
import Constants from 'config/Constants';
import { Switch } from 'components/Forms/Switch';
import { useTranslation } from 'react-i18next';
import { handleAuthenticationChange } from 'features/security/securitySlice';
import { useDispatch } from 'react-redux';

const targetOrigin = '*';

export function Embed() {
    const [status, setStatus] = useState('iframe booting...');
    const areaId = useGetAreaId();
    const metaTitle = getEnv('META_TITLE');
    const dispatch = useDispatch();
    const apiUrl = getEnv('AUTH_API_URL');

    // Send handshake "iframe:ready" to parent
    const notifyReady = useCallback(() => {
        window.parent.postMessage({ type: 'iframe:ready' }, targetOrigin);
    }, []);

    const fetchToken = useCallback(async (apiKey, body) => {
        const res = await fetch(`${apiUrl}/login-iframe`, {
            method: 'POST',
            body: JSON.stringify(body),
            headers: {
                authorization: `Bearer ${apiKey}`,
            },
        });

        if (res.status === 200) {
            return await res.json();
        }

        return undefined;
    }, []);

    // Luister naar token berichten
    useEffect(() => {
        const onMessage = (event) => {
            // Strikte origin check
            // if (event.origin !== targetOrigin) return;
            if (event.data?.type !== 'auth:token') return;

            const { token, email } = event.data;

            if (token.startsWith('sk_') === false) {
                return;
            }

            fetchToken(token, {
                email,
            }).then((res) => {
                if (res && res.hasOwnProperty('token')) {
                    saveTokenData(res.token, '').then(() => {
                        // Login OK
                        dispatch(handleAuthenticationChange());

                        // tokenRef.current = event.data.token;
                        setStatus(() => {
                            const exp = res.exp ? new Date(res.exp * 1000).toLocaleTimeString() : 'onbekend';
                            return `token ontvangen (exp: ${exp})`;
                        });
                        setStatus('ok');

                        // Optioneel: ACK terugsturen
                        window.parent.postMessage({ type: 'iframe:ack' }, targetOrigin);
                    });
                }
            });
        };

        window.addEventListener('message', onMessage);
        notifyReady();

        return () => window.removeEventListener('message', onMessage);
    }, [notifyReady]);

    return (
        <div>
            <div
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 40,
                    overflowY: 'auto',
                }}
            >
                {status === 'ok' && (
                    <div className="p-4">
                        <RenderArea areaId={areaId} />
                    </div>
                )}
            </div>

            <div
                className="text-center text-secondary small bg-white border-top"
                style={{ position: 'absolute', left: 0, right: 0, bottom: 0, zIndex: 100, height: 40, paddingTop: 10 }}
            >
                Powered by{' '}
                <a href="https://www.docrevolution.nl/" target="_blank">
                    {metaTitle}
                </a>
            </div>
        </div>
    );
}

function RenderArea({ areaId }) {
    const [hideChanges, setHideChanges] = useLocalStorage('hide_changes', false);
    const [activeVariantId, setActiveVariantId] = useState(0);
    const [selectedBlockId, setSelectedBlockId] = useState();
    const area = useGetArea(areaId);
    const baseVariant = useGetBaseVariant();
    const document = useGetDocument();

    useEffect(() => {
        if (baseVariant && activeVariantId === 0) {
            setActiveVariantId(baseVariant.id);
        }
    }, [baseVariant]);

    if (!area || !document || activeVariantId === 0) {
        return <LoadingSpinner />;
    }

    return (
        <AreaContext.Provider
            value={{
                activeVariantId,
                setActiveVariantId,
            }}
        >
            <EditAreaContext.Provider
                value={{
                    hideChanges,
                    setHideChanges,
                    selectedBlockId,
                    setSelectedBlockId,
                }}
            >
                <RenderAreaInner area={area} baseVariant={baseVariant} document={document} />
            </EditAreaContext.Provider>
        </AreaContext.Provider>
    );
}

function RenderAreaInner({ area, baseVariant, document }) {
    const { t } = useTranslation();
    const { areaData } = useGetAreaBlocks(true);
    const areaBlocks = areaData?.areaBlocks ?? [];
    const { hideChanges, setHideChanges } = useContext(EditAreaContext);

    const blocksToRender = useMemo(() => {
        const blockIdsInLayout = DocumentHelper.getBlockIdsInLayout(areaBlocks);

        if (blockIdsInLayout.length === 0) {
            return areaBlocks;
        }

        return areaBlocks.filter((block) => !blockIdsInLayout.includes(block.key));
    }, [areaBlocks]);

    return (
        <div>
            <Formik
                enableReinitialize={true}
                validateOnChange={false}
                initialValues={{ ...areaData.area, areaBlocks: areaData.areaBlocks, changes: [] }}
                onSubmit={() => {}}
            >
                {() => (
                    <div
                        className="edit-area-content"
                        style={{
                            overflowY: 'auto',
                            height: 'auto',
                        }}
                    >
                        <div className="flex-center mb-3">
                            <VariantViewButtons area={area} />

                            {document.status !== Constants.documentStatus.draft && (
                                <div className="ml-4">
                                    <Switch
                                        label={t('documents:document.navbar.main.editor.left.blocks.highlightChanges')}
                                        name="show-changes-switch"
                                        checked={!hideChanges}
                                        onChange={() => setHideChanges(!hideChanges)}
                                        disabled={area.edits === 0}
                                    />
                                </div>
                            )}
                        </div>

                        <div className="edit-area-content-shadow position-relative mb-4">
                            <div className="dr-container-body first">
                                <AreaTitle
                                    area={area}
                                    isEditMode={false}
                                    baseVariant={baseVariant}
                                    document={document}
                                    showHistorySidebar={false}
                                />
                            </div>
                            <AreaBlocks
                                area={area}
                                areaId={area.id}
                                areaBlocks={areaBlocks}
                                blocksToRender={blocksToRender}
                                canMoveBlocks={false}
                            />
                        </div>
                    </div>
                )}
            </Formik>
        </div>
    );
}
